import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const EASE = [0.22, 1, 0.36, 1];

export const Eyebrow = ({ children, className = "" }) => (
  <div className={`flex items-center gap-4 ${className}`}>
    <span className="h-px w-10 bg-gold/70" />
    <span className="text-[11px] font-medium uppercase tracking-[0.35em] text-gold">
      {children}
    </span>
  </div>
);

export const LineReveal = ({
  lines,
  inView = false,
  delay = 0,
  className = "",
  lineClassName = "",
}) => {
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, margin: "-40px" });
  const [force, setForce] = useState(false);

  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => {
      const el = ref.current;
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) setForce(true);
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [inView]);

  const show = inView ? seen || force : true;

  return (
    <span ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
          <motion.span
            className={`block ${lineClassName}`}
            initial={{ y: "110%" }}
            animate={show ? { y: "0%" } : { y: "110%" }}
            transition={{
              duration: 0.9,
              delay: show ? delay + i * 0.12 : 0,
              ease: EASE,
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

export const FadeUp = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const SectionHead = ({ eyebrow, titleLines, description, className = "" }) => (
  <div className={className}>
    <FadeUp>
      <Eyebrow>{eyebrow}</Eyebrow>
    </FadeUp>
    <h2 className="mt-6 font-serif text-4xl leading-[1.05] text-[#F5F5F5] sm:text-5xl lg:text-6xl">
      <LineReveal inView lines={titleLines} />
    </h2>
    {description && (
      <FadeUp delay={0.2}>
        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-[#A3A3A3] md:text-lg">
          {description}
        </p>
      </FadeUp>
    )}
  </div>
);
